import React, { useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Toast from '../components/Toast';
import ColisCard from '../components/ColisCard';
import ModalPrompt from '../components/ModalPrompt';
import { useApp } from '../data/ColisContext';

function ActionBtn({ children, onClick, disabled, bg, color }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: '10px 14px',
        borderRadius: 12,
        border: 'none',
        cursor: disabled ? 'not-allowed' : 'pointer',
        fontWeight: 900,
        fontSize: 13,
        fontFamily: 'inherit',
        background: disabled ? '#E2E8F0' : (bg || 'linear-gradient(135deg, #4F46E5, #8B5CF6)'),
        color: disabled ? '#64748b' : (color || 'white'),
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </button>
  );
}

function isValiseColis(c) {
  const id = String(c?.id || '').toUpperCase();
  if (id.startsWith('VAL-')) return true;
  const type = String(c?.meta?.type || '').toLowerCase();
  return type === 'valise';
}

export default function MesValisesPage() {
  const navigate = useNavigate();
  const { user, colis, adminColis, updateStatut, affecterLivreur, showToast } = useApp();
  const [selectedId, setSelectedId] = useState('');
  const [livreurModal, setLivreurModal] = useState({ open: false, colisId: '' });

  const isMobile = typeof window !== 'undefined'
    ? window.matchMedia && window.matchMedia('(max-width: 768px)').matches
    : false;

  const isAdmin = !!user?.is_admin;
  const isAgent = !!user?.agent;

  if (!isAdmin && !isAgent) {
    return <Navigate to="/" replace />;
  }

  const sourceList = isAdmin ? adminColis : colis;

  const valises = useMemo(() => {
    return (Array.isArray(sourceList) ? sourceList : [])
      .filter(isValiseColis)
      .sort((a, b) => String(b?.date || '').localeCompare(String(a?.date || '')));
  }, [sourceList]);

  const selectedValise = useMemo(() => {
    if (!selectedId) return null;
    return valises.find(v => String(v?.id) === String(selectedId)) || null;
  }, [selectedId, valises]);

  const contentIds = useMemo(() => {
    const raw = selectedValise?.meta?.contents;
    if (!Array.isArray(raw)) return [];
    return raw.map(x => String(x || '').trim()).filter(Boolean);
  }, [selectedValise]);

  const contentColis = useMemo(() => {
    if (!selectedValise) return [];
    const byId = new Map((Array.isArray(sourceList) ? sourceList : []).map(c => [String(c?.id), c]));
    return contentIds
      .map(id => byId.get(id))
      .filter(Boolean)
      .sort((a, b) => String(b?.date || '').localeCompare(String(a?.date || '')));
  }, [contentIds, selectedValise, sourceList]);

  const canAdvanceChildren = !!(selectedValise && String(selectedValise?.statut || '') === 'dispatching');

  const nextStep = (statut) => {
    if (statut === 'dispatching') return { key: 'affecte', label: '✅ Affecté à un livreur' };
    if (statut === 'affecte') return { key: 'en_route', label: '🚚 En route' };
    if (statut === 'en_route') return { key: 'livre', label: '🎉 Livré' };
    return null;
  };

  const safeUpdate = async (id, statut) => {
    try {
      if (typeof updateStatut !== 'function') {
        showToast('Action indisponible (frontend à redéployer)', 'error');
        return;
      }
      await updateStatut(id, statut);
      showToast('Statut mis à jour', 'success');
    } catch (e) {
      showToast(e?.message || 'Erreur', 'error');
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', fontFamily: "'DM Sans', sans-serif" }}>
      <Navbar />

      <main style={{ maxWidth: 1200, margin: '0 auto', padding: '28px 20px' }}>
        <button onClick={() => navigate(-1)} style={{
          background: 'none', border: 'none', cursor: 'pointer',
          color: '#4F46E5', fontWeight: 600, fontSize: 14,
          marginBottom: 22, display: 'flex', alignItems: 'center', gap: 6,
          fontFamily: 'inherit', padding: 0,
        }}>
          ← Retour
        </button>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 22, fontWeight: 900, color: '#0f172a' }}>Mes valises</div>
            <div style={{ fontSize: 13, color: '#64748b', fontWeight: 700, marginTop: 4 }}>
              {isAdmin ? 'Toutes les valises' : 'Vos valises'}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => navigate('/depot')}
              style={{
                padding: '12px 14px',
                background: 'linear-gradient(135deg, #4F46E5, #8B5CF6)',
                color: 'white',
                border: 'none',
                borderRadius: 12,
                fontWeight: 900,
                fontSize: 14,
                cursor: 'pointer',
                fontFamily: 'inherit',
                whiteSpace: 'nowrap',
                boxShadow: '0 4px 12px rgba(79,70,229,0.25)',
                width: isMobile ? '100%' : undefined,
              }}
            >
              ➕ Créer un colis
            </button>
          </div>
        </div>

        {valises.length === 0 ? (
          <div style={{
            background: 'white',
            borderRadius: 18,
            padding: 50,
            textAlign: 'center',
            color: '#94a3b8',
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
          }}>
            <div style={{ fontSize: 44, marginBottom: 10 }}>🧳</div>
            <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 8, color: '#1e293b' }}>Aucune valise</div>
            <div style={{ fontSize: 14 }}>Créez une valise depuis le formulaire de création colis.</div>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '380px 1fr', gap: 16, alignItems: 'start' }}>
            <div style={{
              background: 'white',
              borderRadius: 18,
              padding: 14,
              boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
              border: '1px solid #f0f0f0',
            }}>
              <div style={{ fontSize: 13, fontWeight: 900, color: '#334155', marginBottom: 10 }}>Valises ({valises.length})</div>

              {isMobile && (
                <select
                  value={selectedId}
                  onChange={(e) => setSelectedId(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px 12px',
                    borderRadius: 14,
                    border: '2px solid #e2e8f0',
                    background: 'white',
                    fontFamily: 'inherit',
                    fontWeight: 800,
                    marginBottom: 10,
                  }}
                >
                  <option value="">Sélectionner une valise...</option>
                  {valises.map(v => {
                    const count = Array.isArray(v?.meta?.contents) ? v.meta.contents.length : 0;
                    return (
                      <option key={v.id} value={v.id}>
                        {v.id} · {count} colis
                      </option>
                    );
                  })}
                </select>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {valises.map(v => {
                  const active = String(selectedId) === String(v.id);
                  const count = Array.isArray(v?.meta?.contents) ? v.meta.contents.length : 0;
                  return (
                    <button
                      key={v.id}
                      type="button"
                      onClick={() => setSelectedId(v.id)}
                      style={{
                        textAlign: 'left',
                        padding: '10px 12px',
                        borderRadius: 14,
                        border: active ? '2px solid #4F46E5' : '2px solid #e2e8f0',
                        background: active ? '#EEF2FF' : '#ffffff',
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                      }}
                    >
                      <div style={{ fontWeight: 900, color: '#0f172a', fontSize: 13 }}>{v.id}</div>
                      <div style={{ fontWeight: 800, color: '#475569', fontSize: 12, marginTop: 2 }}>{v.nom || 'Valise'} · {count} colis</div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {selectedValise ? (
                <>
                  <ColisCard colis={selectedValise} />

                  <div style={{
                    background: 'white',
                    borderRadius: 18,
                    padding: 14,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                    border: '1px solid #f0f0f0',
                  }}>
                    <div style={{ fontSize: 13, fontWeight: 900, color: '#334155', marginBottom: 10 }}>
                      Colis dans la valise ({contentColis.length})
                    </div>

                    {contentColis.length === 0 ? (
                      <div style={{ color: '#94a3b8', fontWeight: 700, fontSize: 13, padding: 18 }}>Aucun colis dans cette valise.</div>
                    ) : (
                      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(340px, 1fr))', gap: 16 }}>
                        {contentColis.map(c => (
                          <div key={c.id} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            <ColisCard colis={c} />

                            {canAdvanceChildren && (
                              <div style={{
                                background: 'white',
                                border: '1px solid #e2e8f0',
                                borderRadius: 16,
                                padding: 12,
                                display: 'flex',
                                gap: 8,
                                flexWrap: 'wrap',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                              }}>
                                <div style={{ fontSize: 12, color: '#64748b', fontWeight: 900 }}>
                                  Workflow Sénégal
                                </div>

                                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                  {(() => {
                                    const step = nextStep(String(c?.statut || ''));
                                    if (!step) return null;
                                    if (step.key === 'affecte') {
                                      return (
                                        <ActionBtn
                                          color="#0369A1"
                                          bg="#E0F2FE"
                                          onClick={() => setLivreurModal({ open: true, colisId: c.id })}
                                        >
                                          {step.label}
                                        </ActionBtn>
                                      );
                                    }
                                    return (
                                      <ActionBtn onClick={() => safeUpdate(c.id, step.key)}>
                                        {step.label}
                                      </ActionBtn>
                                    );
                                  })()}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div style={{
                  background: 'white',
                  borderRadius: 18,
                  padding: 40,
                  color: '#94a3b8',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                  border: '1px solid #f0f0f0',
                  textAlign: 'center',
                }}>
                  Sélectionnez une valise à gauche.
                </div>
              )}
            </div>
          </div>
        )}
      </main>

      <ModalPrompt
        open={livreurModal.open}
        title="🛵 Affecter à un livreur"
        description="Saisissez le nom du livreur (obligatoire)."
        placeholder="Ex: Mamadou"
        confirmLabel="Affecter"
        onClose={() => setLivreurModal({ open: false, colisId: '' })}
        onConfirm={async (name) => {
          const id = livreurModal.colisId;
          const n = String(name || '').trim();
          if (!id) return;
          if (!n) {
            showToast('Nom requis', 'error');
            return;
          }
          try {
            if (typeof affecterLivreur !== 'function') {
              showToast('Action indisponible (frontend à redéployer)', 'error');
              return;
            }
            await affecterLivreur(id, n);
            setLivreurModal({ open: false, colisId: '' });
          } catch (e) {
            showToast(e?.message || 'Erreur', 'error');
          }
        }}
      />

      <Toast />
    </div>
  );
}
